import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ThemeContext } from '../contexts/ThemeContext';
import { BRAND, brandPalette } from '../theme/brand';

export interface Testimonial {
  quote: string;
  author: string;
  title: string;
  avatar?: string;
}

interface TestimonialsSliderProps {
  testimonials: Testimonial[];
  autoScroll?: boolean;
  interval?: number;
}

const AVATAR_GRADIENTS = [
  'linear-gradient(135deg, #C92BB5, #6D1B77)',
  'linear-gradient(135deg, #FCC632, #F59E0B)',
  'linear-gradient(135deg, #4FC3F7, #6D1B77)',
  'linear-gradient(135deg, #FF8A80, #C92BB5)',
];

// 3D "lapozás": a kártya forgással jön be és megy ki
const slideVariants = {
  enter: (dir: number) => ({ opacity: 0, x: dir * 70, rotateY: dir * 35, scale: 0.92 }),
  center: { opacity: 1, x: 0, rotateY: 0, scale: 1 },
  exit: (dir: number) => ({ opacity: 0, x: dir * -70, rotateY: dir * -35, scale: 0.92 }),
};

const TestimonialsSlider: React.FC<TestimonialsSliderProps> = ({
  testimonials,
  autoScroll = true,
  interval = 5000,
}) => {
  const { darkMode } = useContext(ThemeContext)!;
  const pal = brandPalette(darkMode);
  const reduce = useReducedMotion();
  const [[index, dir], setPage] = useState<[number, number]>([0, 1]);
  const [paused, setPaused] = useState(false);
  const count = testimonials.length;
  const touchX = useRef<number | null>(null);

  const go = useCallback(
    (step: number) => setPage(([i]) => [(i + step + count) % count, step]),
    [count]
  );

  useEffect(() => {
    if (!autoScroll || paused || count < 2) return;
    const t = setInterval(() => go(1), interval);
    return () => clearInterval(t);
  }, [autoScroll, paused, interval, go, count]);

  if (count === 0) return null;
  const item = testimonials[index];

  return (
    <Box
      sx={{ width: '100%', maxWidth: 680, mx: 'auto', textAlign: 'center' }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={(e) => {
        touchX.current = e.touches[0].clientX;
        setPaused(true);
      }}
      onTouchEnd={(e) => {
        if (touchX.current !== null) {
          const diff = e.changedTouches[0].clientX - touchX.current;
          if (Math.abs(diff) > 45) go(diff < 0 ? 1 : -1);
        }
        touchX.current = null;
        setPaused(false);
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 2 } }}>
        <IconButton
          aria-label="Előző vélemény"
          onClick={() => go(-1)}
          sx={{ color: pal.heading, display: { xs: 'none', sm: 'inline-flex' }, flexShrink: 0 }}
        >
          <ChevronLeftIcon fontSize="large" />
        </IconButton>

        <Box sx={{ flex: 1, perspective: '1000px', minHeight: { xs: 280, sm: 250 }, position: 'relative' }}>
          <AnimatePresence initial={false} custom={dir} mode="wait">
            <motion.div
              key={index}
              custom={dir}
              variants={reduce ? undefined : slideVariants}
              initial={reduce ? { opacity: 0 } : 'enter'}
              animate={reduce ? { opacity: 1 } : 'center'}
              exit={reduce ? { opacity: 0 } : 'exit'}
              transition={{ duration: 0.45 }}
              style={{ transformStyle: 'preserve-3d' }}
            >
              <Box
                sx={{
                  position: 'relative',
                  px: { xs: 2.5, sm: 4 },
                  pt: 6,
                  pb: 3,
                  borderRadius: '28px',
                  background: darkMode
                    ? 'linear-gradient(160deg, rgba(109,27,119,0.55), rgba(29,18,71,0.92))'
                    : 'linear-gradient(160deg, #FFFFFF, #F7E9FA)',
                  border: pal.cardBorder,
                  boxShadow: darkMode
                    ? '0 8px 0 rgba(18,10,57,0.8), 0 22px 40px rgba(0,0,0,0.45)'
                    : '0 8px 0 rgba(109,27,119,0.15), 0 22px 40px rgba(109,27,119,0.18)',
                }}
              >
                {/* Avatár */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: -30,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    background: item.avatar ? `url(${item.avatar}) center/cover` : AVATAR_GRADIENTS[index % AVATAR_GRADIENTS.length],
                    border: `4px solid ${darkMode ? '#1d1247' : '#FFFFFF'}`,
                    boxShadow: '0 8px 16px rgba(0,0,0,0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontWeight: 800,
                    fontSize: 26,
                  }}
                >
                  {!item.avatar && item.author.trim().charAt(0).toUpperCase()}
                </Box>

                <Typography
                  aria-hidden
                  sx={{ position: 'absolute', top: 30, left: 18, fontSize: 64, lineHeight: 1, color: BRAND.yellow, opacity: 0.35, fontFamily: 'Georgia, serif' }}
                >
                  “
                </Typography>

                <Typography
                  sx={{
                    color: pal.text,
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    lineHeight: 1.65,
                    fontStyle: 'italic',
                    mb: 2.5,
                  }}
                >
                  {item.quote}
                </Typography>

                <Typography sx={{ color: pal.heading, fontWeight: 800, fontSize: '1.05rem' }}>{item.author}</Typography>
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    mt: 0.5,
                    px: 1.5,
                    py: 0.25,
                    borderRadius: 99,
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    color: darkMode ? BRAND.purpleDark : '#FFFFFF',
                    backgroundColor: darkMode ? BRAND.yellow : BRAND.purple,
                  }}
                >
                  {item.title}
                </Box>
              </Box>
            </motion.div>
          </AnimatePresence>
        </Box>

        <IconButton
          aria-label="Következő vélemény"
          onClick={() => go(1)}
          sx={{ color: pal.heading, display: { xs: 'none', sm: 'inline-flex' }, flexShrink: 0 }}
        >
          <ChevronRightIcon fontSize="large" />
        </IconButton>
      </Box>

      {/* Pöttyök */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 3 }}>
        {testimonials.map((_, i) => (
          <Box
            key={i}
            component="button"
            aria-label={`${i + 1}. vélemény`}
            onClick={() => setPage([i, i > index ? 1 : -1])}
            sx={{
              width: i === index ? 26 : 10,
              height: 10,
              borderRadius: 99,
              border: 0,
              p: 0,
              cursor: 'pointer',
              transition: 'all 0.3s',
              backgroundColor: i === index ? pal.accent : darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(109,27,119,0.25)',
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

export default TestimonialsSlider;